import i18n, { currentLocale } from '../../../i18n';
import { dateLocale } from '../../../lib/localePrefs';
import type {
  CapacityBasis,
  PerfAggregation,
  PerfCompareKind,
  PerfFrameUnit,
  PerfSettings,
} from '../types';

// Shared validation + defaults for the global Performance aggregation / frame
// settings. Mirrored on the backends (Node: server/opsCommand/perfSettings.ts,
// Python: opscommand/perf_settings.py) - keep the three in sync.

export const AGGREGATIONS: PerfAggregation[] = [
  'day',
  'week',
  'month',
  'quarter',
  'year',
];

/**
 * One comparison follows the person across every screen (filtering audit,
 * decision 7.5.3), with one meaning per kind everywhere:
 * - `off`: no comparison;
 * - `prior`: the period just before, same length;
 * - `yoy_weekday`: last year, same weekdays (364 days back, a Saturday meets a Saturday);
 * - `yoy`: last year, same dates (the calendar dates one year back);
 * - `custom`: dates the person picked.
 * Mirrored by the server's `perf_settings.COMPARE_KINDS`.
 */
export const COMPARE_KINDS: PerfCompareKind[] = ['off', 'prior', 'yoy_weekday', 'yoy', 'custom'];

/** The comparison on every screen until the person picks one, Performance included. */
export const DEFAULT_COMPARE_KIND: PerfCompareKind = 'yoy_weekday';

/** Days back of "last year, same weekdays": 52 weeks, so the weekday stays. */
export const YOY_WEEKDAY_SHIFT_DAYS = 364;

export const COMPARE_KIND_LABELS: Record<PerfCompareKind, string> = {
  get off() {
    return i18n.t('chrome:compareKind.off');
  },
  get prior() {
    return i18n.t('chrome:compareKind.prior');
  },
  get yoy_weekday() {
    return i18n.t('chrome:compareKind.yoyWeekday');
  },
  get yoy() {
    return i18n.t('chrome:compareKind.yoy');
  },
  get custom() {
    return i18n.t('chrome:compareKind.custom');
  },
};

// The frame ("last X …") unit list. Quarter is intentionally not offered as a
// frame unit (operators express ranges in days/weeks/months/years); a quarter
// aggregation simply uses a year frame.
export const FRAME_UNITS: PerfFrameUnit[] = ['day', 'week', 'month', 'year'];

export const CAPACITY_BASES: CapacityBasis[] = ['seats', 'online', 'physical'];

export const CAPACITY_BASIS_LABELS: Record<CapacityBasis, string> = {
  get seats() {
    return i18n.t('chrome:capacity.seats');
  },
  get online() {
    return i18n.t('chrome:capacity.online');
  },
  get physical() {
    return i18n.t('chrome:capacity.physical');
  },
};

/** Hover help for the capacity-basis toggle in the Performance topbar. */
export const CAPACITY_BASIS_TIPS: Record<CapacityBasis, string> = {
  get seats() {
    return i18n.t('chrome:capacity.seatsTip');
  },
  get online() {
    return i18n.t('chrome:capacity.onlineTip');
  },
  get physical() {
    return i18n.t('chrome:capacity.physicalTip');
  },
};

export const DEFAULT_CAPACITY_BASIS: CapacityBasis = 'seats';

// Coarseness rank - a frame unit may never be finer than the aggregation
// bucket (that is the "monthly for last 5 days" illogical case).
const UNIT_RANK: Record<PerfAggregation | PerfFrameUnit, number> = {
  day: 0,
  week: 1,
  month: 2,
  quarter: 3,
  year: 4,
};

// Approximate calendar days per bucket / per frame unit, used only to estimate
// how many buckets a frame yields (the authoritative bucket edges are computed
// server-side from real calendar math).
const BUCKET_DAYS: Record<PerfAggregation, number> = {
  day: 1,
  week: 7,
  month: 30.44,
  quarter: 91.31,
  year: 365.25,
};

const FRAME_DAYS: Record<PerfFrameUnit, number> = {
  day: 1,
  week: 7,
  month: 30.44,
  year: 365.25,
};

export const MIN_BUCKETS = 1;
export const MAX_BUCKETS = 120;

export const AGGREGATION_LABELS: Record<PerfAggregation, string> = {
  get day() {
    return i18n.t('chrome:aggregation.day');
  },
  get week() {
    return i18n.t('chrome:aggregation.week');
  },
  get month() {
    return i18n.t('chrome:aggregation.month');
  },
  get quarter() {
    return i18n.t('chrome:aggregation.quarter');
  },
  get year() {
    return i18n.t('chrome:aggregation.year');
  },
};

export const DEFAULT_PERF_SETTINGS: PerfSettings = {
  aggregation: 'week',
  frameCount: 4,
  frameUnit: 'week',
  capacityBasis: DEFAULT_CAPACITY_BASIS,
};

export function normalizeCapacityBasis(value: unknown): CapacityBasis {
  return CAPACITY_BASES.includes(value as CapacityBasis)
    ? (value as CapacityBasis)
    : DEFAULT_CAPACITY_BASIS;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isDateRange(s: PerfSettings): boolean {
  return !!(
    s.dateStart &&
    s.dateEnd &&
    DATE_RE.test(s.dateStart) &&
    DATE_RE.test(s.dateEnd) &&
    s.dateStart <= s.dateEnd
  );
}

export function isCompareDateRange(s: PerfSettings): boolean {
  return !!(
    s.compareDateStart &&
    s.compareDateEnd &&
    DATE_RE.test(s.compareDateStart) &&
    DATE_RE.test(s.compareDateEnd) &&
    s.compareDateStart <= s.compareDateEnd
  );
}

export function normalizeCompareKind(value: unknown): PerfCompareKind | undefined {
  return COMPARE_KINDS.includes(value as PerfCompareKind)
    ? (value as PerfCompareKind)
    : undefined;
}

/** The same on every screen and for every period: last year, same weekdays. */
export function defaultCompareKind(_s?: PerfSettings): PerfCompareKind {
  return DEFAULT_COMPARE_KIND;
}

/**
 * The comparison every screen shows: the person's choice, made on any
 * screen, else the default. A kind that was stored without being chosen (a
 * former default such as "previous period") is not a choice and gives way
 * to today's default.
 */
export function effectiveCompareKind(s: PerfSettings): PerfCompareKind {
  if (!s.compareExplicit) return DEFAULT_COMPARE_KIND;
  return normalizeCompareKind(s.compareKind) ?? DEFAULT_COMPARE_KIND;
}

/**
 * The period settings Growth sends to the server. The comparison goes only
 * when the person chose it; the server resolves the same default otherwise.
 */
export function growthRequestSettings(s: PerfSettings): Record<string, unknown> {
  const base: Record<string, unknown> = {
    aggregation: s.aggregation,
    frameCount: s.frameCount,
    frameUnit: s.frameUnit,
    dateStart: s.dateStart,
    dateEnd: s.dateEnd,
  };
  if (!s.compareExplicit) return base;
  return {
    ...base,
    compareKind: effectiveCompareKind(s),
    compareDateStart: s.compareDateStart,
    compareDateEnd: s.compareDateEnd,
  };
}

function isoShiftDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y!, m! - 1, d! + days));
  return date.toISOString().slice(0, 10);
}

/** The same calendar date one year earlier; 29 February meets 28 February (as the server's add_months). */
function isoLastYear(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const last = new Date(Date.UTC(y! - 1, m!, 0)).getUTCDate();
  return `${String(y! - 1).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(Math.min(d!, last)).padStart(2, '0')}`;
}

/** A date range's comparison dates for a year-over-year kind, as the server computes them. */
export function yearOverYearRange(
  kind: 'yoy' | 'yoy_weekday',
  start: string,
  end: string,
): { start: string; end: string } {
  return kind === 'yoy'
    ? { start: isoLastYear(start), end: isoLastYear(end) }
    : { start: isoShiftDays(start, -YOY_WEEKDAY_SHIFT_DAYS), end: isoShiftDays(end, -YOY_WEEKDAY_SHIFT_DAYS) };
}

export function formatRangeLabel(start: string, end: string): string {
  const fmt = (iso: string) => {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString(dateLocale(currentLocale()), {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };
  const a = fmt(start);
  // One day (a day-grain compare point) is just that day.
  if (start === end) return a;
  const b = fmt(end);
  const startYear = start.slice(0, 4);
  const endYear = end.slice(0, 4);
  if (startYear === endYear && a.endsWith(endYear)) {
    return `${a.replace(` ${endYear}`, '')} – ${b}`;
  }
  return `${a} – ${b}`;
}

/**
 * A payload's comparison window named in the person's language from its
 * dates ("15. Sept. – 12. Okt. 2025"), never the server's own wording, which
 * read "15 September – 12 Oktober 2025" on German pages or stayed English.
 */
export function localizeComparePeriod<
  T extends {
    comparePeriod?: { label: string; start?: string; end?: string } | null;
    periodLabel?: string;
    periodStart?: string;
    periodEnd?: string;
  },
>(payload: T | null | undefined): T | null | undefined {
  if (!payload) return payload;
  const isDay = (value: unknown): value is string => typeof value === 'string' && DATE_RE.test(value);
  let next = payload;
  const cp = payload.comparePeriod;
  if (cp && isDay(cp.start) && isDay(cp.end)) {
    const label = formatRangeLabel(cp.start, cp.end);
    if (label !== cp.label) next = { ...next, comparePeriod: { ...cp, label } };
  }
  // The period the scorecard names, likewise ("14. Sept. – 11. Okt. 2026").
  if (payload.periodLabel && isDay(payload.periodStart) && isDay(payload.periodEnd)) {
    const label = formatRangeLabel(payload.periodStart, payload.periodEnd);
    if (label !== payload.periodLabel) next = { ...next, periodLabel: label };
  }
  return next;
}

export function compareDeltaLabel(
  comparePeriod?: { label: string } | null,
): string {
  return comparePeriod?.label
    ? i18n.t('chrome:frame.vsLabel', { label: comparePeriod.label })
    : i18n.t('chrome:frame.vsPrior');
}

export function comparePeriodSummary(
  primaryLabel: string,
  comparePeriod?: { label: string } | null,
  aggregationLabel?: string,
): string {
  const parts: string[] = [
    i18n.t('chrome:frame.lookingAt', { label: primaryLabel }),
  ];
  if (aggregationLabel) {
    parts.push(i18n.t('chrome:frame.brokenInto', { unit: aggregationLabel }));
  }
  if (comparePeriod?.label) {
    parts.push(i18n.t('chrome:frame.comparedTo', { label: comparePeriod.label }));
  }
  return parts.join(' · ');
}

// Sensible default frame for each aggregation, applied when the operator
// switches aggregation into a combination the current frame can't satisfy.
const DEFAULT_FRAME: Record<
  PerfAggregation,
  { frameCount: number; frameUnit: PerfFrameUnit }
> = {
  day: { frameCount: 14, frameUnit: 'day' },
  week: { frameCount: 4, frameUnit: 'week' },
  month: { frameCount: 6, frameUnit: 'month' },
  quarter: { frameCount: 2, frameUnit: 'year' },
  year: { frameCount: 3, frameUnit: 'year' },
};

export function frameUnitsFor(aggregation: PerfAggregation): PerfFrameUnit[] {
  // Also drop units where a single frame already exceeds MAX_BUCKETS
  // (e.g. day × year ≈ 365 bars). Those combos used to slip past coerce via
  // frameCountBounds forcing max=min=1 even when rawMax was 0.
  return FRAME_UNITS.filter(u => {
    if (UNIT_RANK[u] < UNIT_RANK[aggregation]) return false;
    const bucketsAtOne = Math.floor(
      FRAME_DAYS[u] / BUCKET_DAYS[aggregation] + 1e-9,
    );
    return bucketsAtOne <= MAX_BUCKETS;
  });
}

export function defaultFrameFor(aggregation: PerfAggregation): {
  frameCount: number;
  frameUnit: PerfFrameUnit;
} {
  return { ...DEFAULT_FRAME[aggregation] };
}

export function bucketCountFor(s: PerfSettings): number {
  const totalDays = FRAME_DAYS[s.frameUnit] * s.frameCount;
  return Math.floor(totalDays / BUCKET_DAYS[s.aggregation] + 1e-9);
}

// Inclusive frame-count bounds that keep the bucket count within
// [MIN_BUCKETS, MAX_BUCKETS] for a given aggregation + frame unit.
export function frameCountBounds(
  aggregation: PerfAggregation,
  frameUnit: PerfFrameUnit,
): { min: number; max: number } {
  const ratio = BUCKET_DAYS[aggregation] / FRAME_DAYS[frameUnit];
  const min = Math.max(1, Math.ceil(MIN_BUCKETS * ratio - 1e-9));
  const rawMax = Math.floor(MAX_BUCKETS * ratio + 1e-9);
  // When rawMax < min the unit cannot host a valid frame (see frameUnitsFor).
  // Keep max >= min only for valid units; callers must not use invalid pairs.
  const max = rawMax < min ? 0 : Math.max(min, rawMax);
  return { min, max };
}

export function validatePerfSettings(s: PerfSettings): {
  ok: boolean;
  bucketCount: number;
  reason?: string;
} {
  if (!Number.isFinite(s.frameCount) || s.frameCount < 1) {
    return {
      ok: false,
      bucketCount: 0,
      reason: i18n.t('chrome:frame.minLength'),
    };
  }
  if (!frameUnitsFor(s.aggregation).includes(s.frameUnit)) {
    return {
      ok: false,
      bucketCount: 0,
      reason: i18n.t('chrome:frame.unitTooShort', {
        frame: s.frameUnit,
        aggregation: s.aggregation,
      }),
    };
  }
  const bucketCount = bucketCountFor(s);
  if (bucketCount < MIN_BUCKETS) {
    return {
      ok: false,
      bucketCount,
      reason: i18n.t('chrome:frame.tooShort', { aggregation: s.aggregation }),
    };
  }
  if (bucketCount > MAX_BUCKETS) {
    return {
      ok: false,
      bucketCount,
      reason: i18n.t('chrome:frame.tooMany', {
        aggregation: s.aggregation,
        max: MAX_BUCKETS,
      }),
    };
  }
  if (effectiveCompareKind(s) === 'custom' && !isCompareDateRange(s)) {
    return {
      ok: false,
      bucketCount,
      reason: i18n.t('chrome:frame.pickCompare'),
    };
  }
  return { ok: true, bucketCount };
}

function coerceCompareFields(next: PerfSettings): PerfSettings {
  const compareKind = effectiveCompareKind(next);
  const compareDateStart =
    typeof next.compareDateStart === 'string' && DATE_RE.test(next.compareDateStart)
      ? next.compareDateStart
      : null;
  const compareDateEnd =
    typeof next.compareDateEnd === 'string' && DATE_RE.test(next.compareDateEnd)
      ? next.compareDateEnd
      : null;

  if (compareKind === 'custom') {
    if (compareDateStart && compareDateEnd && compareDateStart <= compareDateEnd) {
      return {
        ...next,
        compareKind: 'custom',
        compareDateStart,
        compareDateEnd,
      };
    }
    return {
      ...next,
      compareKind: 'off',
      compareDateStart: null,
      compareDateEnd: null,
    };
  }

  return {
    ...next,
    compareKind,
    compareDateStart: compareKind === 'off' ? null : compareDateStart,
    compareDateEnd: compareKind === 'off' ? null : compareDateEnd,
  };
}

function unitNoun(unit: PerfFrameUnit, count: number): string {
  return i18n.t(
    count === 1 ? `chrome:units.${unit}One` : `chrome:units.${unit}Many`,
  );
}

function aggregationNoun(agg: PerfAggregation, plural: boolean): string {
  return i18n.t(
    plural ? `chrome:aggregation.${agg}Plural` : `chrome:aggregation.${agg}`,
  );
}

/**
 * User-facing reason when coerce snapped a lookback because of MAX_BUCKETS
 * (or the derived frame-count bounds). Returns null when nothing was clamped
 * for that reason (date ranges are uncapped here).
 */
export function lookbackClampNotice(
  requested: PerfSettings,
  applied: PerfSettings,
): string | null {
  if (isDateRange(requested) || isDateRange(applied)) return null;

  const reqAgg = AGGREGATIONS.includes(requested.aggregation)
    ? requested.aggregation
    : applied.aggregation;
  const reqUnit = FRAME_UNITS.includes(requested.frameUnit)
    ? requested.frameUnit
    : applied.frameUnit;
  const reqCount = Number.isFinite(requested.frameCount)
    ? Math.max(1, Math.round(requested.frameCount))
    : applied.frameCount;

  // Unit dropped: one frame already exceeds the bucket cap (e.g. day × year).
  if (
    UNIT_RANK[reqUnit] >= UNIT_RANK[reqAgg] &&
    !frameUnitsFor(reqAgg).includes(reqUnit)
  ) {
    const bucketsAtOne = Math.floor(
      FRAME_DAYS[reqUnit] / BUCKET_DAYS[reqAgg] + 1e-9,
    );
    if (bucketsAtOne > MAX_BUCKETS) {
      return i18n.t('chrome:frame.clampUnit', {
        reqCount,
        reqUnit: unitNoun(reqUnit, reqCount),
        reqAgg: aggregationNoun(reqAgg, true),
        buckets: bucketsAtOne * reqCount,
        max: MAX_BUCKETS,
        appliedCount: applied.frameCount,
        appliedUnit: unitNoun(applied.frameUnit, applied.frameCount),
      });
    }
  }

  // Count clamped for the current aggregation × unit.
  if (
    reqAgg === applied.aggregation &&
    frameUnitsFor(reqAgg).includes(reqUnit) &&
    reqUnit === applied.frameUnit &&
    reqCount !== applied.frameCount
  ) {
    const { min, max } = frameCountBounds(reqAgg, reqUnit);
    if (reqCount > max) {
      return i18n.t('chrome:frame.clampMax', {
        agg: aggregationNoun(reqAgg, false),
        max,
        unit: unitNoun(reqUnit, max),
        bucketMax: MAX_BUCKETS,
      });
    }
    if (reqCount < min) {
      return i18n.t('chrome:frame.clampMin', {
        agg: aggregationNoun(reqAgg, false),
        min,
        unit: unitNoun(reqUnit, min),
      });
    }
  }

  // Aggregation switch forced a different frame (unit no longer valid).
  if (
    requested.aggregation !== applied.aggregation &&
    (reqUnit !== applied.frameUnit || reqCount !== applied.frameCount) &&
    !frameUnitsFor(applied.aggregation).includes(reqUnit)
  ) {
    const bucketsAtOne = Math.floor(
      FRAME_DAYS[reqUnit] / BUCKET_DAYS[applied.aggregation] + 1e-9,
    );
    if (bucketsAtOne > MAX_BUCKETS) {
      return i18n.t('chrome:frame.clampSwitch', {
        reqCount,
        reqUnit: unitNoun(reqUnit, reqCount),
        agg: aggregationNoun(applied.aggregation, true),
        buckets: bucketsAtOne * reqCount,
        max: MAX_BUCKETS,
        appliedCount: applied.frameCount,
        appliedUnit: unitNoun(applied.frameUnit, applied.frameCount),
      });
    }
  }

  return null;
}

// Coerce arbitrary settings into a valid combination: snap the frame to the
// aggregation default when the unit is incompatible, then clamp the count.
export function coercePerfSettings(s: PerfSettings): PerfSettings {
  let next: PerfSettings = { ...s };
  if (!frameUnitsFor(next.aggregation).includes(next.frameUnit)) {
    const def = defaultFrameFor(next.aggregation);
    next = { ...next, ...def };
  }
  let { min, max } = frameCountBounds(next.aggregation, next.frameUnit);
  if (max < min) {
    // Invalid unit slipped through (e.g. stale URL day×year) — snap to default.
    const def = defaultFrameFor(next.aggregation);
    next = { ...next, ...def };
    ({ min, max } = frameCountBounds(next.aggregation, next.frameUnit));
  }
  next.frameCount = Math.min(max, Math.max(min, Math.round(next.frameCount)));
  const capacityBasis = normalizeCapacityBasis(next.capacityBasis);
  next = coerceCompareFields({ ...next, capacityBasis });

  if (isDateRange(next)) {
    return {
      aggregation: next.aggregation,
      frameCount: next.frameCount,
      frameUnit: next.frameUnit,
      dateStart: next.dateStart,
      dateEnd: next.dateEnd,
      capacityBasis: next.capacityBasis,
      compareKind: next.compareKind,
      compareDateStart: next.compareDateStart,
      compareDateEnd: next.compareDateEnd,
      ...(next.compareExplicit ? { compareExplicit: true } : {}),
    };
  }
  const { dateStart: _ds, dateEnd: _de, ...frameOnly } = next;
  return frameOnly;
}

export function frameLabel(s: PerfSettings): string {
  if (isDateRange(s)) {
    return formatRangeLabel(s.dateStart!, s.dateEnd!);
  }
  const unitKey =
    s.frameCount === 1
      ? `chrome:units.${s.frameUnit}One`
      : `chrome:units.${s.frameUnit}Many`;
  return i18n.t(
    s.frameCount === 1 ? 'chrome:frame.lastOne' : 'chrome:frame.lastMany',
    { count: s.frameCount, unit: i18n.t(unitKey) },
  );
}

export function compareFrameLabel(s: PerfSettings): string | null {
  const kind = effectiveCompareKind(s);
  if (kind === 'off') return null;
  if (kind === 'prior') return i18n.t('chrome:compareKind.prior');
  if (kind === 'yoy' || kind === 'yoy_weekday') {
    if (isDateRange(s)) {
      const range = yearOverYearRange(kind, s.dateStart!, s.dateEnd!);
      return formatRangeLabel(range.start, range.end);
    }
    return i18n.t(kind === 'yoy' ? 'chrome:compareKind.yoy' : 'chrome:compareKind.yoyWeekdayShort');
  }
  if (kind === 'custom' && isCompareDateRange(s)) {
    return formatRangeLabel(s.compareDateStart!, s.compareDateEnd!);
  }
  return null;
}

/** Plain-English sentence mirroring the Looking at → Broken into → Compared to controls. */
export function perfSettingsStory(
  s: PerfSettings,
  opts?: { omitCompare?: boolean },
): string {
  const when = isDateRange(s)
    ? formatRangeLabel(s.dateStart!, s.dateEnd!)
    : i18n.t('chrome:frame.lastThe', {
        count: s.frameCount,
        unit: unitNoun(s.frameUnit, s.frameCount),
      });
  const grain = aggregationNoun(s.aggregation, true);
  const kind = effectiveCompareKind(s);
  const compareRaw = compareFrameLabel(s);
  const compare =
    compareRaw &&
    (kind === 'prior'
      ? i18n.t('chrome:compareKind.priorInStory')
      : kind === 'yoy_weekday' && !isDateRange(s)
        ? i18n.t('chrome:compareKind.yoyWeekdayInStory')
        : kind === 'yoy' && !isDateRange(s)
          ? i18n.t('chrome:compareKind.yoyInStory')
          : compareRaw);
  if (opts?.omitCompare) {
    return i18n.t('chrome:frame.story', { when, grain });
  }
  if (compare) {
    return i18n.t('chrome:frame.storyWithCompare', { when, grain, compare });
  }
  return i18n.t('chrome:frame.storyNoCompare', { when, grain });
}

/** Consequence line: how many trend bars the current settings imply. */
export function perfSettingsConsequence(s: PerfSettings): string | null {
  if (isDateRange(s)) return null;
  const n = bucketCountFor(s);
  if (n < 2) return null;
  const grain = AGGREGATION_LABELS[s.aggregation];
  return i18n.t('chrome:frame.bars', { count: n, grain });
}
